import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/* =============================================================================
   ThemeProvider — Multi-tenant white-label
   ----------------------------------------------------------------------------
   Responsabilidades:
     1. Hidratar el branding del tenant inmediatamente después del login
        (GET /api/v1/me) y exponerlo vía contexto.
     2. Inyectar variables CSS (--brand-primary, --brand-primary-fg, …) en :root
        para que CUALQUIER componente pueda usarlas sin pasar props.
     3. Persistir el último theme en sessionStorage para evitar el "flash" del
        branding genérico mientras se resuelve el primer fetch.
     4. Exponer un selector de "Cliente Final" (EndClient) para que el dashboard
        pueda decir "Gestión de Proyectos para [Cliente Final]".
     5. Permitir override manual (`setTheme`) para usar en login screens o
        admin panels que cambien de tenant en caliente.
   ============================================================================= */

const STORAGE_KEY = "atelier.theme.v1";

/** Theme por defecto: el de la propia plataforma (genérico). */
const DEFAULT_THEME = Object.freeze({
  brandName: "Atelier CRM",
  brandLogoUrl: null,
  primaryColor: "#0F172A",
});

/** Estructura del contexto que verán los consumidores. */
const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  user: null,
  endClient: null,
  setTheme: (_next) => {},      // eslint-disable-line @typescript-eslint/no-unused-vars
  setEndClient: (_ec) => {},     // eslint-disable-line @typescript-eslint/no-unused-vars
  refresh: async () => {},
  loading: false,
  error: null,
});

/* ----------------------------------------------------------------------------
   Helpers de color: derivar foreground (negro/blanco) según luminancia
   y un acento más oscuro para hover. Mantiene contraste WCAG accesible.
---------------------------------------------------------------------------- */
function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  let h = m[1];
  if (h.length === 3) h = h.split("").map(c => c + c).join("");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}
function relLuminance({ r, g, b }) {
  const f = c => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
function readableForeground(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#FFFFFF";
  return relLuminance(rgb) > 0.5 ? "#0F172A" : "#FFFFFF";
}
function shade(hex, amt) {
  // amt < 0 oscurece, > 0 aclara (rango -1 .. 1)
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const adj = c => {
    const t = amt < 0 ? 0 : 255;
    const p = Math.abs(amt);
    return Math.round((t - c) * p + c);
  };
  const to2 = n => n.toString(16).padStart(2, "0");
  return `#${to2(adj(rgb.r))}${to2(adj(rgb.g))}${to2(adj(rgb.b))}`;
}

/** Aplica el theme al `:root` del documento como variables CSS. */
function applyThemeToRoot(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--brand-primary",       theme.primaryColor);
  root.style.setProperty("--brand-primary-fg",    readableForeground(theme.primaryColor));
  root.style.setProperty("--brand-primary-hover", shade(theme.primaryColor, -0.12));
  root.style.setProperty("--brand-primary-soft",  shade(theme.primaryColor,  0.92));
}

/* ----------------------------------------------------------------------------
   Provider
---------------------------------------------------------------------------- */
export function ThemeProvider({
  children,
  apiBaseUrl = "/api/v1",
  /** Permite inyectar un fetcher custom (testing, mock, JWT injection, …). */
  fetchMe = null,
  /** Si ya tienes los datos al montar (SSR / preload), los aceptas aquí. */
  initialMe = null,
}) {
  const [user, setUser] = useState(initialMe?.userId ? initialMe : null);
  const [endClient, setEndClient] = useState(null);

  // Hidratar desde sessionStorage para evitar flash del genérico.
  const [theme, setThemeState] = useState(() => {
    if (initialMe?.tenant?.branding) return normalize(initialMe.tenant.branding);
    if (typeof window !== "undefined") {
      try {
        const cached = window.sessionStorage.getItem(STORAGE_KEY);
        if (cached) return normalize(JSON.parse(cached));
      } catch (_) {}
    }
    return DEFAULT_THEME;
  });

  const [loading, setLoading] = useState(!initialMe);
  const [error, setError] = useState(null);

  // Aplicar al :root cada vez que cambia.
  useEffect(() => { applyThemeToRoot(theme); }, [theme]);

  const persist = useCallback((t) => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    } catch (_) {}
  }, []);

  const setTheme = useCallback((next) => {
    const t = normalize(next);
    setThemeState(t);
    persist(t);
  }, [persist]);

  /** Llama al endpoint /me y refresca user + theme. Idempotente. */
  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const me = fetchMe
        ? await fetchMe()
        : await defaultFetchMe(apiBaseUrl);
      setUser(me);
      if (me?.tenant?.branding) setTheme(me.tenant.branding);
      return me;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, fetchMe, setTheme]);

  // Auto-fetch al montar si no recibimos initialMe.
  useEffect(() => {
    if (!initialMe) refresh().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    theme, user, endClient,
    setTheme, setEndClient,
    refresh, loading, error,
  }), [theme, user, endClient, setTheme, refresh, loading, error]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/* ----------------------------------------------------------------------------
   Hooks de consumo
---------------------------------------------------------------------------- */
export const useTheme     = () => useContext(ThemeContext);
export const useBranding  = () => useContext(ThemeContext).theme;
export const useEndClient = () => {
  const { endClient, setEndClient } = useContext(ThemeContext);
  return [endClient, setEndClient];
};

/* ----------------------------------------------------------------------------
   Componentes presentacionales (opcionales pero útiles)
---------------------------------------------------------------------------- */

/**
 * <BrandMark /> — renderiza logo + nombre del tenant. Reemplaza el monograma
 * genérico del Sidebar/Topbar. Si no hay logo, cae a un cuadrado con la
 * primera letra del brandName, coloreado con el primary del tenant.
 */
export function BrandMark({ size = "md", showName = true, className = "" }) {
  const { brandName, brandLogoUrl, primaryColor } = useBranding();
  const initials = (brandName ?? "—").trim().slice(0, 1).toUpperCase();
  const dim = size === "lg" ? "h-10 w-10 text-base" : "h-8 w-8 text-sm";
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      {brandLogoUrl ? (
        <img src={brandLogoUrl} alt={brandName ?? "Brand"}
             className={`${dim} rounded-lg object-cover ring-1 ring-slate-200`} />
      ) : (
        <span
          className={`${dim} grid place-items-center rounded-lg font-serif italic text-[var(--brand-primary-fg)]`}
          style={{ background: primaryColor }}
        >
          {initials}
        </span>
      )}
      {showName && (
        <span className="leading-tight">
          <span className="block text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
            White-label
          </span>
          <span className="block text-sm font-semibold text-slate-900">{brandName}</span>
        </span>
      )}
    </span>
  );
}

/**
 * <BrandButton /> — botón primario que usa el color del tenant.
 * Añade hover y foreground accesible automáticamente.
 */
export function BrandButton({ children, className = "", ...rest }) {
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium
                  transition shadow-sm hover:opacity-95 ${className}`}
      style={{
        background: "var(--brand-primary)",
        color: "var(--brand-primary-fg)",
      }}
    >
      {children}
    </button>
  );
}

/**
 * <DashboardTitle /> — encabezado del dashboard que cumple el requerimiento:
 *   "Gestión de Proyectos para [Nombre del Cliente Final]".
 * Si no hay endClient seleccionado, usa el brandName del tenant.
 */
export function DashboardTitle({ fallback }) {
  const [endClient] = useEndClient();
  const { brandName } = useBranding();
  const subject = endClient?.name ?? fallback ?? brandName;
  return (
    <div>
      <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400">
        Espacio de trabajo
      </p>
      <h1 className="font-serif text-2xl tracking-tight text-slate-900 md:text-[28px]">
        Gestión de Proyectos para <span className="italic">{subject}</span>
      </h1>
    </div>
  );
}

/* ----------------------------------------------------------------------------
   Internals
---------------------------------------------------------------------------- */

function normalize(branding) {
  return {
    brandName:    branding?.brandName    ?? branding?.brand_name    ?? DEFAULT_THEME.brandName,
    brandLogoUrl: branding?.brandLogoUrl ?? branding?.brand_logo_url ?? null,
    primaryColor: branding?.primaryColor ?? branding?.primary_color  ?? DEFAULT_THEME.primaryColor,
  };
}

/** Fetcher por defecto. Asume que el JWT ya está en `Authorization` (interceptor del cliente HTTP). */
async function defaultFetchMe(base) {
  const token = (typeof window !== "undefined")
    ? window.localStorage.getItem("accessToken")
    : null;
  const res = await fetch(`${base}/me`, {
    headers: {
      "Accept": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: "include",
  });
  if (!res.ok) throw new Error(`/me failed: ${res.status}`);
  return res.json();
}

export default ThemeProvider;
